import {useState, useCallback, useMemo, useEffect, useRef} from 'react';
import styled from 'styled-components';
import {
	DndContext,
	DragOverlay,
	closestCorners,
	PointerSensor,
	KeyboardSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
	type DragOverEvent,
	type DragStartEvent,
} from '@dnd-kit/core';
import {arrayMove, sortableKeyboardCoordinates} from '@dnd-kit/sortable';
import type {Ticket, TicketStatus} from '../../types/ticket';
import type {MentionUser} from '../../hooks/useMentionUsers';
import {BOARD_STATUSES} from '../../constants/boardColumns';
import {groupTicketsByStatus} from '../../utils/groupTicketsByStatus';
import {canDragTicket} from '../../utils/canDragTicket';
import {useDeleteTicket} from '../../hooks/useDeleteTicket';
import {DeleteTicketModal} from '../DeleteTicketModal';
import {BoardColumn} from './BoardColumn';
import {BoardToast} from './BoardToast';
import {TicketCardPreview} from './TicketCard';
import UpdateTicketMutation from '../../../../../../mutations/ticketing/tickets/updateTicketMutation';
import {commitTicketingMutation} from '../../../../graphql/forecastRelay';
import {isPermissionError, isNotFoundError} from '../../utils/mutationError';

type Role = 'agent' | 'lead' | 'admin';

export interface PersistResult {
	ok: boolean;
	status?: number; // 403 / 404 mapped from the GraphQL error, when known
}

export type PersistTicketPatch = {status: TicketStatus} | {assigneeId: string | null; handoffNote?: string};

export type PersistTicket = (ticketId: string, patch: PersistTicketPatch) => Promise<PersistResult>;

interface Props {
	tickets: Ticket[];
	me: {sub: string; role: Role};
	/** TICKETS_MANAGE — gates drag-to-change-status, card assign and resolve. */
	canManage: boolean;
	hasMore: boolean;
	onMutate: () => void;
	users?: MentionUser[];
	/**
	 * Relay client id of the tickets connection the board is rendering. Delete hands it to
	 * `@deleteEdge` so the card leaves the store; omitting it makes that directive a no-op
	 * and the card only disappears once the caller's refetch lands.
	 */
	connectionId?: string;
}

const TruncationNotice = styled.div`
	margin-bottom: ${p => p.theme.sys.spacing[300]};
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	border: ${p => p.theme.sys.size.borderwidth.thin} solid ${p => p.theme.sys.color.border.decorative};
	background: ${p => p.theme.sys.color.background.neutral['2']};
	padding: ${p => p.theme.sys.spacing[200]} ${p => p.theme.sys.spacing[400]};
	${p => p.theme.sys.font?.subheading?.['2']}
	color: ${p => p.theme.sys.color.text.content.main};
`;

const Columns = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[300]};
	@media (min-width: 640px) {
		flex-direction: row;
		flex-wrap: wrap;
		gap: ${p => p.theme.sys.spacing[400]};
	}
	@media (min-width: 768px) {
		flex-wrap: nowrap;
		align-items: flex-start;
		overflow-x: auto;
		padding-bottom: ${p => p.theme.sys.spacing[400]};
	}
`;

const DragOverlayCard = styled(TicketCardPreview)`
	width: 256px;
	/* Tilted and lifted, so the thing under the cursor is clearly a copy in transit
	   rather than the card still sitting in its column. */
	transform: rotate(1deg);
	cursor: grabbing;
`;

export interface BuildDragEndHandlerOpts {
	getTickets: () => Ticket[];
	setTickets: (updater: (curr: Ticket[]) => Ticket[]) => void;
	setToast: (msg: string) => void;
	can: (t: Ticket) => boolean;
	onMutate: () => void;
	persist: PersistTicket;
}

export interface BuildAssignHandlerOpts {
	getTickets: () => Ticket[];
	setTickets: (updater: (curr: Ticket[]) => Ticket[]) => void;
	setToast: (msg: string) => void;
	users: MentionUser[];
	onMutate: () => void;
	persist: PersistTicket;
}

// Minimal seam over `commitTicketingMutation` so its exact shape can be tested
// without driving a real drag/assign through the rendered board (impractical in
// jsdom). Mirrors `mutationModule.commit(env, input, onSuccess, onError)`.
type CommitMutation = (
	module: typeof UpdateTicketMutation,
	input: {input: PersistTicketPatch & {id: string}},
	onSuccess: () => void,
	onError: (err: unknown) => void
) => void;

export interface BuildPersistOpts {
	getTickets: () => Ticket[];
	commit: CommitMutation;
}

// The board owns mutation-error UX end-to-end: every handler optimistically
// applies a change, then on failure reverts and shows an inline toast (see
// buildDragEndHandler / buildAssignHandler). So `persist` deliberately overrides
// Forecast's default correlation-ID error modal — it passes its own `onError` to
// commitTicketingMutation, resolving to a coarse 403/404/undefined status that the
// handlers map to a toast. The seam's signature exposes no `onFailure` to forward,
// and unknown (non-403/404) errors intentionally surface as a generic retry toast
// rather than a modal, keeping all board errors inline.
export function buildPersist(opts: BuildPersistOpts): PersistTicket {
	return (ticketId, patch) => {
		const target = opts.getTickets().find(t => t.id === ticketId);
		const globalId = target?.globalId ?? ticketId;
		return new Promise<PersistResult>(resolve => {
			opts.commit(
				UpdateTicketMutation,
				{input: {id: globalId, ...patch}},
				() => resolve({ok: true}),
				err =>
					resolve({
						ok: false,
						status: isPermissionError(err) ? 403 : isNotFoundError(err) ? 404 : undefined,
					})
			);
		});
	};
}

function statusFromColumnId(id: string | number | undefined | null): TicketStatus | null {
	if (typeof id !== 'string') return null;
	if (id.startsWith('column:')) {
		const s = id.slice('column:'.length) as TicketStatus;
		return BOARD_STATUSES.includes(s) ? s : null;
	}
	return null;
}

function statusFromDragTarget(id: string | number | undefined | null, tickets: readonly Ticket[]): TicketStatus | null {
	const columnStatus = statusFromColumnId(id);
	if (columnStatus) return columnStatus;
	if (typeof id !== 'string') return null;
	return tickets.find(t => t.id === id)?.status ?? null;
}

function reorderWithinStatus(tickets: readonly Ticket[], ticketId: string, overTicketId: string) {
	const ticket = tickets.find(t => t.id === ticketId);
	const overTicket = tickets.find(t => t.id === overTicketId);
	if (!ticket || !overTicket || ticket.status !== overTicket.status || ticket.id === overTicket.id) {
		return [...tickets];
	}

	const statusTickets = tickets.filter(t => t.status === ticket.status);
	const oldIndex = statusTickets.findIndex(t => t.id === ticketId);
	const newIndex = statusTickets.findIndex(t => t.id === overTicketId);
	if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return [...tickets];

	const reordered = arrayMove(statusTickets, oldIndex, newIndex);
	let nextIndex = 0;
	return tickets.map(t => (t.status === ticket.status ? reordered[nextIndex++]! : t));
}

/**
 * Writes a new status, applying it immediately and putting it back if the write fails.
 *
 * `verb` is the action the reader took — `move`, `resolve` — and appears in the failure
 * toasts, so a rejected write explains what did not happen rather than naming the field
 * that changed underneath.
 */
export function buildStatusChangeHandler(opts: BuildDragEndHandlerOpts) {
	return async function changeStatus(ticketId: string, nextStatus: TicketStatus, verb: string) {
		const tickets = opts.getTickets();
		const previous = tickets;
		opts.setTickets(curr => curr.map(t => (t.id === ticketId ? {...t, status: nextStatus} : t)));

		try {
			const res = await opts.persist(ticketId, {status: nextStatus});
			if (!res.ok) {
				opts.setTickets(() => previous);
				if (res.status === 403) {
					opts.setToast(`You no longer have permission to ${verb} this ticket.`);
				} else if (res.status === 404) {
					opts.setToast('Ticket no longer exists.');
				} else {
					opts.setToast(`Couldn't ${verb} ticket. Try again.`);
				}
				opts.onMutate();
				return;
			}
			opts.onMutate();
		} catch {
			opts.setTickets(() => previous);
			opts.setToast(`Couldn't ${verb} ticket. Check your connection.`);
		}
	};
}

export function buildDragEndHandler(opts: BuildDragEndHandlerOpts) {
	const changeStatus = buildStatusChangeHandler(opts);

	return async function handleDragEnd(event: DragEndEvent) {
		const ticketId = event.active.id;
		if (typeof ticketId !== 'string') return;

		const tickets = opts.getTickets();
		const overId = event.over?.id;
		const overTicket = typeof overId === 'string' ? tickets.find(t => t.id === overId) : undefined;

		let nextStatus = statusFromColumnId(event.over?.id);
		if (!nextStatus && overTicket) {
			nextStatus = overTicket.status;
		}
		if (!nextStatus) return;

		const ticket = tickets.find(t => t.id === ticketId);
		if (!ticket) return;
		if (!opts.can(ticket)) return;
		if (ticket.status === nextStatus) {
			if (overTicket) {
				opts.setTickets(curr => reorderWithinStatus(curr, ticketId, overTicket.id));
			}
			return;
		}

		await changeStatus(ticketId, nextStatus, 'move');
	};
}

export function buildAssignHandler(opts: BuildAssignHandlerOpts) {
	return async function handleAssign(ticketId: string, userId: string | null, note?: string) {
		const tickets = opts.getTickets();
		const ticket = tickets.find(t => t.id === ticketId);
		if (!ticket) return;

		const assignedUser = userId ? opts.users.find(u => u.id === userId) : null;
		const previous = tickets;

		opts.setTickets(curr =>
			curr.map(t =>
				t.id === ticketId
					? {
							...t,
							assignee_id: userId,
							assignee_name: assignedUser?.name ?? null,
					  }
					: t
			)
		);

		try {
			const patch: PersistTicketPatch =
				note && note.trim().length > 0 ? {assigneeId: userId, handoffNote: note} : {assigneeId: userId};
			const res = await opts.persist(ticketId, patch);
			if (!res.ok) {
				opts.setTickets(() => previous);
				if (res.status === 403) {
					opts.setToast('You no longer have permission to reassign this ticket.');
				} else if (res.status === 404) {
					opts.setToast('Ticket no longer exists.');
				} else {
					opts.setToast("Couldn't update assignee. Try again.");
				}
				opts.onMutate();
				return;
			}
			opts.onMutate();
		} catch {
			opts.setTickets(() => previous);
			opts.setToast("Couldn't update assignee. Check your connection.");
		}
	};
}

export function BoardView({tickets, canManage, hasMore, onMutate, users = [], connectionId = ''}: Props) {
	const [localTickets, setLocalTickets] = useState<Ticket[]>(tickets);
	const [activeId, setActiveId] = useState<string | null>(null);
	const [overStatus, setOverStatus] = useState<TicketStatus | null>(null);
	const [toast, setToast] = useState<string | null>(null);

	const lastSeenRef = useRef(tickets);
	useEffect(() => {
		if (lastSeenRef.current !== tickets) {
			lastSeenRef.current = tickets;
			setLocalTickets(tickets);
		}
	}, [tickets]);

	const grouped = useMemo(() => groupTicketsByStatus(localTickets), [localTickets]);
	const activeTicket = useMemo(() => localTickets.find(ticket => ticket.id === activeId) ?? null, [activeId, localTickets]);
	const activeStatus = activeTicket?.status ?? null;

	const sensors = useSensors(
		useSensor(PointerSensor, {activationConstraint: {distance: 4}}),
		useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
	);

	// Drag is gated on TICKETS_MANAGE alone, matching what the gateway enforces. `can` is
	// invoked as can(ticket) by buildDragEndHandler/BoardColumn; the argument is ignored
	// since the permission is per-user, not per-ticket.
	const can = useCallback(() => canDragTicket(canManage), [canManage]);

	// Board errors are handled inline (optimistic revert + toast), not via
	// Forecast's default error modal — see buildPersist for the full rationale.
	const persist = useMemo<PersistTicket>(
		() => buildPersist({getTickets: () => localTickets, commit: commitTicketingMutation}),
		[localTickets]
	);

	const persistDragEnd = useMemo(
		() =>
			buildDragEndHandler({
				getTickets: () => localTickets,
				setTickets: setLocalTickets,
				setToast,
				can,
				onMutate,
				persist,
			}),
		[localTickets, can, onMutate, persist]
	);

	const persistStatusChange = useMemo(
		() =>
			buildStatusChangeHandler({
				getTickets: () => localTickets,
				setTickets: setLocalTickets,
				setToast,
				can,
				onMutate,
				persist,
			}),
		[localTickets, can, onMutate, persist]
	);

	const persistAssign = useMemo(
		() =>
			buildAssignHandler({
				getTickets: () => localTickets,
				setTickets: setLocalTickets,
				setToast,
				users,
				onMutate,
				persist,
			}),
		[localTickets, users, onMutate, persist]
	);

	const {submit: deleteTicket, loading: deleting} = useDeleteTicket();
	// The board addresses cards by local id (that is what dnd-kit and the kebab menu carry),
	// so the target is resolved back to its ticket for both the confirmation copy and the
	// mutation input.
	const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
	const deleteTargetTicket = useMemo(
		() => (deleteTarget === null ? null : localTickets.find(t => t.id === deleteTarget) ?? null),
		[deleteTarget, localTickets]
	);

	const handleAssign = useCallback(
		(ticketId: string, userId: string | null, note?: string) => {
			void persistAssign(ticketId, userId, note);
		},
		[persistAssign]
	);

	// The card moves to the Resolved column as soon as the optimistic write lands, the
	// same as if it had been dragged there.
	const handleResolve = useCallback(
		(ticketId: string) => {
			void persistStatusChange(ticketId, 'resolved', 'resolve');
		},
		[persistStatusChange]
	);

	const handleDeleteRequest = useCallback((ticketId: string) => {
		setDeleteTarget(ticketId);
	}, []);

	const handleDeleteConfirm = useCallback(() => {
		// The mutation input is decoded server-side with fromGlobalId, which does not
		// validate — a card's local id would decode to garbage and delete nothing, so a
		// target that cannot be resolved to its global id is dropped rather than sent.
		if (!deleteTargetTicket) {
			setDeleteTarget(null);
			return;
		}
		deleteTicket(deleteTargetTicket.globalId, connectionId ? [connectionId] : []).then(
			() => {
				setDeleteTarget(null);
				onMutate();
			},
			() => {
				setDeleteTarget(null);
			}
		);
	}, [deleteTargetTicket, deleteTicket, connectionId, onMutate]);

	const handleDragStart = useCallback((event: DragStartEvent) => {
		const ticketId = event.active.id;
		setActiveId(typeof ticketId === 'string' ? ticketId : null);
	}, []);

	const handleDragOver = useCallback(
		(event: DragOverEvent) => {
			setOverStatus(statusFromDragTarget(event.over?.id, localTickets));
		},
		[localTickets]
	);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			setActiveId(null);
			setOverStatus(null);
			void persistDragEnd(event);
		},
		[persistDragEnd]
	);

	const handleDragCancel = useCallback(() => {
		setActiveId(null);
		setOverStatus(null);
	}, []);

	return (
		<>
			{hasMore && <TruncationNotice>Showing first 100. Apply filters to narrow.</TruncationNotice>}

			<DndContext
				sensors={sensors}
				collisionDetection={closestCorners}
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}
				onDragCancel={handleDragCancel}
			>
				<Columns>
					{BOARD_STATUSES.map(status => (
						<BoardColumn
							key={status}
							status={status}
							tickets={grouped[status]}
							canDragTicket={can}
							showDropPlaceholder={activeTicket !== null && overStatus === status && activeStatus !== status}
							users={users}
							onAssign={canManage ? handleAssign : undefined}
							onResolve={canManage ? handleResolve : undefined}
							onDelete={canManage ? handleDeleteRequest : undefined}
						/>
					))}
				</Columns>
				<DragOverlay>{activeTicket ? <DragOverlayCard ticket={activeTicket} /> : null}</DragOverlay>
			</DndContext>

			<BoardToast message={toast} onDismiss={() => setToast(null)} />

			<DeleteTicketModal
				open={deleteTarget !== null}
				ticketSlug={deleteTargetTicket?.slug ?? ''}
				ticketSubject={deleteTargetTicket?.subject ?? undefined}
				loading={deleting}
				onConfirm={handleDeleteConfirm}
				onCancel={() => setDeleteTarget(null)}
			/>
		</>
	);
}
